import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '../utils/logger'
import {
  ArrowLeft, 
  Save, 
  Code, 
  Globe, 
  Settings,
  Eye
} from 'lucide-react'
import { templateService } from '../services/templateService'
import { CreateTemplateRequest, ValidationType } from '../types/template.types'
import toast from 'react-hot-toast'

const TemplateCreate: React.FC = () => {
  const navigate = useNavigate()
  const [isCreating, setIsCreating] = useState(false)
  const [validationTypes, setValidationTypes] = useState<ValidationType[]>([])
  const [showPreview, setShowPreview] = useState(false)

  // Formulário de criação
  const [formData, setFormData] = useState<CreateTemplateRequest>({
    name: '',
    description: '',
    template: '',
    template_type: 'single',
    format: 'text',
    api_url: '',
    api_token: '',
    parent_template_id: undefined,
    input_prompt: '',
    input_validation: 'none',
    // Campos para controle de tentativas
    attempts_control_enabled: false,
    max_attempts_per_month: 2,
    attempts_reset_period: 'monthly'
  })

  useEffect(() => {
    loadValidationTypes()
  }, [])

  const loadValidationTypes = async () => {
    try {
      const response = await templateService.getValidationTypes()
      if (response.success) {
        setValidationTypes(response.data)
      }
    } catch (error) {
      logger.error('Erro ao carregar tipos de validação:', error)
    }
  }

  const handleCreateTemplate = async () => {
    try {
      setIsCreating(true)
      const response = await templateService.createTemplate(formData)
      
      if (response.success) {
        toast.success('Template criado com sucesso!')
        navigate('/templates')
      } else {
        toast.error('Erro ao criar template')
      }
    } catch (error) {
      logger.error('Erro ao criar template:', error)
      toast.error('Erro ao criar template')
    } finally {
      setIsCreating(false)
    }
  }


  const getTemplateTypeIcon = (type: string) => {
    switch (type) {
      case 'single':
        return <Code className="w-4 h-4" />
      case 'list_detail':
        return <Settings className="w-4 h-4" />
      case 'custom_flow':
        return <Globe className="w-4 h-4" />
      case 'api_dynamic':
        return <Globe className="w-4 h-4" />
      default:
        return <Code className="w-4 h-4" />
    }
  }

  const getTemplateTypeLabel = (type: string) => {
    switch (type) {
      case 'single':
        return 'Simples'
      case 'list_detail':
        return 'Lista/Detalhe'
      case 'custom_flow':
        return 'Fluxo Personalizado'
      case 'api_dynamic':
        return 'API Dinâmica'
      default:
        return type
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => navigate('/templates')}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Criar Template
              </h1>
              <p className="text-gray-600">Crie um novo template de resposta personalizado</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Formulário */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <form onSubmit={(e) => {
                e.preventDefault()
                handleCreateTemplate()
              }} className="space-y-6">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Consulta de Dados"
                    required
                  />
                </div>

                {/* Descrição */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Descrição do template"
                  />
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo *
                  </label>
                  <select
                    value={formData.template_type}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, template_type: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="single">Simples</option>
                    <option value="list_detail">Lista/Detalhe</option>
                    <option value="custom_flow">Fluxo Personalizado</option>
                    <option value="api_dynamic">API Dinâmica</option>
                  </select>
                </div>

                {/* Formato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Formato
                  </label>
                  <select
                    value={formData.format}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, format: e.target.value as 'text' | 'markdown' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="text">Texto</option>
                    <option value="markdown">Markdown</option>
                  </select>
                </div>

                {/* Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template *
                  </label>
                  <textarea
                    value={formData.template}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, template: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={8}
                    placeholder="Digite o template usando variáveis como {{variavel}}"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use variáveis como {`{{nome}}`}, {`{{data}}`}, {`{{#each items}}`} para loops
                  </p>
                </div>

                {/* URL da API - Apenas para single e list_detail */}
                {formData.template_type === 'single' || formData.template_type === 'list_detail' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL da API
                    </label>
                    <input
                      type="url"
                      value={formData.api_url}
                      onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, api_url: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://exemplo.com/api/endpoint"
                    />
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          <strong>Configuração de API:</strong> Para templates do tipo <strong>{formData.template_type}</strong>, 
                          as configurações de API (URL e Token) devem ser definidas no <strong>Flow Config</strong> associado.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Token da API - Apenas para single e list_detail */}
                {formData.template_type === 'single' || formData.template_type === 'list_detail' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                    Token da API
                  </label>
                    <input
                      type="text"
                      value={formData.api_token}
                      onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, api_token: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Token de autenticação"
                    />
                  </div>
                ) : null}

                {/* Prompt de Entrada */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prompt de Entrada
                  </label>
                  <textarea
                    value={formData.input_prompt}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, input_prompt: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Digite o CPF/CNPJ para consultar os dados"
                  />
                </div>

                {/* Validação de Entrada */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Validação de Entrada
                  </label>
                  <select
                    value={formData.input_validation}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, input_validation: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {validationTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} - {type.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Controle de Tentativas */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    🔒 Controle de Tentativas
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Checkbox para habilitar controle */}
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="attempts_control_enabled"
                        checked={formData.attempts_control_enabled || false}
                        onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ 
                          ...prev, 
                          attempts_control_enabled: e.target.checked 
                        }))}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <label htmlFor="attempts_control_enabled" className="text-sm font-medium text-gray-700">
                          Habilitar controle de tentativas
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          Limita quantas vezes um cliente pode usar este template
                        </p>
                      </div>
                    </div>

                    {/* Configurações quando habilitado */}
                    {formData.attempts_control_enabled && (
                      <div className="ml-7 space-y-4 bg-gray-50 p-4 rounded-lg">
                        {/* Máximo de tentativas */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Máximo de Tentativas
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={formData.max_attempts_per_month || 2}
                            onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ 
                              ...prev, 
                              max_attempts_per_month: parseInt(e.target.value) || 2 
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="2"
                          />
                        </div>

                        {/* Período de reset */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Período de Reset
                          </label>
                          <select
                            value={formData.attempts_reset_period || 'monthly'}
                            onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ 
                              ...prev, 
                              attempts_reset_period: e.target.value as 'monthly' | 'weekly' | 'daily' 
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="daily">Diário</option>
                            <option value="weekly">Semanal</option>
                            <option value="monthly">Mensal</option>
                          </select>
                        </div>

                        {/* Exemplos de configuração */}
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Exemplos de Configuração:</h4>
                          <ul className="text-xs text-blue-800 space-y-1">
                            <li>• <strong>Desbloqueio:</strong> 2 tentativas/mês</li>
                            <li>• <strong>Consulta:</strong> 5 tentativas/dia</li>
                            <li>• <strong>Relatório:</strong> 10 tentativas/semana</li>
                            <li>• <strong>Boletos:</strong> Sem limite (desabilitado)</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/templates')}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    <span>{isCreating ? 'Criando...' : 'Criar Template'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Preview */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Preview</h3>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center space-x-2 px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4" />
                  <span>{showPreview ? 'Ocultar' : 'Mostrar'}</span>
                </button>
              </div>

              {showPreview && (
                <div className="space-y-4">
                  {/* Informações do Template */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      {getTemplateTypeIcon(formData.template_type)}
                      <span className="font-medium text-gray-900">
                        {formData.name || 'Nome do Template'}
                      </span>
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        {getTemplateTypeLabel(formData.template_type)}
                      </span>
                    </div>
                    {formData.description && (
                      <p className="text-sm text-gray-600 mb-2">{formData.description}</p>
                    )}
                    <div className="text-xs text-gray-500">
                      <span>Formato: {formData.format}</span>
                      {formData.api_url && (
                        <span className="ml-2 flex items-center space-x-1">
                          <Globe className="w-3 h-3" />
                          <span>API Externa</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Preview do Template */}
                  {formData.template && (
                    <div className="border rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Conteúdo:</h4>
                      <div className="bg-gray-50 rounded p-3 text-sm font-mono whitespace-pre-wrap">
                        {formData.template}
                      </div>
                    </div>
                  )}

                  {/* Controle de Tentativas */}
                  {formData.attempts_control_enabled && (
                    <div className="border rounded-lg p-4 bg-orange-50">
                      <h4 className="text-sm font-medium text-orange-900 mb-2 flex items-center">
                        🔒 Controle de Tentativas
                      </h4>
                      <div className="text-xs text-orange-800">
                        <p>Máximo: {formData.max_attempts_per_month || 2} tentativas</p>
                        <p>Período: {formData.attempts_reset_period === 'daily' ? 'Diário' : formData.attempts_reset_period === 'weekly' ? 'Semanal' : 'Mensal'}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TemplateCreate



